import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import type { SupportedLocale } from '@/lib/translation/types';
import { routing } from '@/lib/translation/routing';
interface LanguageSwitcherProps {
  className?: string;
  defaultLocale: SupportedLocale;
}

/**
 * A component that allows the user to switch the language.
 */
export const LanguageSwitcher = ({
  className,
  defaultLocale
}: LanguageSwitcherProps) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Initialize with defaultLocale but update based on current domain
  const [locale, setLocale] = useState<SupportedLocale>(defaultLocale);

  // Detect current locale based on domain when component mounts
  useEffect(() => {
    // Get full host including port for development environments
    const currentHost = window.location.host; // includes hostname:port

    // Find matching domain in routing config
    const domainConfig = routing.domains?.find(d => d.domain === currentHost);
    if (domainConfig?.defaultLocale) {
      setLocale(domainConfig.defaultLocale);
    } else if (process.env.NODE_ENV === 'production') {
      // Fallback for production if exact match not found
      if (currentHost === 'istitutoimage.com') {
        setLocale('en');
      } else if (currentHost === 'istitutoimage.it') {
        setLocale('it');
      }
    }
  }, []);
  const toggleLanguage = () => {
    const newLocale = locale === 'en' ? 'it' : 'en';

    // Update state first
    setLocale(newLocale);

    // Find the target domain for the new locale
    const domain = routing.domains?.find(d => d.defaultLocale === newLocale)?.domain;
    if (!domain) {
      return;
    }

    // Construct the URL with the current path and search params
    const url = `${pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;

    // Navigate to the new domain
    window.location.href = `${window.location.protocol}//${domain}${url}`;
  };

  // Use the className in the component's root element
  return <div className={className} data-sentry-component="LanguageSwitcher" data-sentry-source-file="language-switcher.tsx">
      <button onClick={toggleLanguage} className="text-black font-medium font-nudica flex items-center text-xs">
        {locale === 'en' ? <>
            <span className="font-bold">EN</span>
            <span className="ml-1 opacity-50">/ IT</span>
          </> : <>
            <span className="font-bold">IT</span>
            <span className="ml-1 opacity-50">/ EN</span>
          </>}
      </button>
    </div>;
};